import * as Dfns from 'date-fns/fp';
import { formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import {
  DATE_FORMAT,
  DeviceBreakoutCompanyData,
  DeviceBreakoutNetworkData,
  DeviceCell,
  DeviceTableRows,
  NetworkOption
} from './device-breakout-constants';

export const initDates = (): DateRange => {
  const currentMonday = Dfns.startOfISOWeek(new Date());
  const start = Dfns.format(DATE_FORMAT)(Dfns.subWeeks(1)(currentMonday));
  const end = Dfns.format(DATE_FORMAT)(Dfns.subDays(1)(currentMonday));
  return { start, end };
};

export const formatNetworkLabel = (option: NetworkOption): string =>
  option.description
    ? `${option.network} ${option.description} ${option.platform}`
    : `${option.network} ${option.platform}`;

const buildCompanyTableRows = (tableData: DeviceBreakoutCompanyData): DeviceTableRows => {
  const { deviceList, networkList, totalImpsByNetwork, networkDeviceMap } = tableData;

  const selectedData: DeviceCell[][] = deviceList.map(device =>
    networkList.map((network): DeviceCell => {
      const count = networkDeviceMap[network][device];
      if (!count) {
        return { count: '-', percentage: '-' };
      }
      return {
        count: formatNumber(count),
        percentage: `(${Math.round((count / totalImpsByNetwork[network]) * 100)}%)`,
        combinedPlacementName: network.replace(/ /g, ''),
        placement: network,
        network: network.split(' ')[0]
      };
    })
  );

  return {
    topData: networkList,
    leftData: deviceList,
    bottomData: networkList.map(network => formatNumber(totalImpsByNetwork[network])),
    selectedData
  };
};

const buildNetworkTableRows = (tableData: DeviceBreakoutNetworkData): DeviceTableRows => {
  const { deviceList, companyList, totalImpsByCompany, companyDeviceMap } = tableData;

  const selectedData: DeviceCell[][] = deviceList.map(device =>
    companyList.map((company): DeviceCell => {
      const count = companyDeviceMap[company][device];
      if (!count) {
        return { count: '-', percentage: '-' };
      }
      return {
        count: formatNumber(count),
        percentage: `(${Math.round((count / totalImpsByCompany[company]) * 100)}%)`
      };
    })
  );

  return {
    topData: companyList,
    leftData: deviceList,
    bottomData: companyList.map(company => formatNumber(totalImpsByCompany[company])),
    selectedData
  };
};

export const buildTableRows = (
  tableData?: DeviceBreakoutCompanyData | DeviceBreakoutNetworkData
): Partial<DeviceTableRows> => {
  if (!tableData) {
    return {};
  }
  return 'companyList' in tableData
    ? buildNetworkTableRows(tableData)
    : buildCompanyTableRows(tableData);
};
